import React, { MutableRefObject, useEffect } from "react";
import CharacterCount from "../../shared/CharacterCount";
import { useLanguage } from "@/contexts";
import { SmallCopyButton } from "../../shared/CopyButton";

interface PlaygroundData {
  title: string;
  description: string;
  selectedModel: string;
  systemMessage: string;
  promptText: string;
  changeNotes: string;
  isSystemPromptExpanded: boolean;
  originalSystemMessage: string;
  enhancedSystemMessage: string;
}

interface ModelInfo {
  id: string | number;
  name: string;
  display_name: string;
  context_window: number;
  input_cost_per_million_tokens?: number;
  output_cost_per_million_tokens?: number;
}

interface PlaygroundTabProps {
  newPrompt: boolean | undefined;
  playgroundData: PlaygroundData;
  setPlaygroundData: React.Dispatch<React.SetStateAction<PlaygroundData>>;
  isEnhanced: boolean;
  hasBeenEnhanced: boolean;
  isEnhancing: boolean;
  isStreaming: boolean;
  smoothEnabled: boolean;
  smoothStreamText: string | null;
  handlePlaygroundEnhancePrompt: () => Promise<void>;
  handleRevertToOriginal: () => void;
  handleToggleToEnhanced: () => void;
  handlePromptSubmit: () => void;
  enhancedPromptText: string;
  enhancementError: string | null;
  models: ModelInfo[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  latestVersion: any;
  displayContent: {
    title: string;
    description: string;
    full_text: string;
    systemprompt: string;
  };
  smoothReset: () => void;
  playgroundPromptRef: MutableRefObject<HTMLTextAreaElement | null>;
  userScrolledAwayRef: MutableRefObject<boolean>;
  // reuse copy handler from parent for system prompt
  onCopyText?: (text: string, type: string) => void;
}

const PlaygroundTab: React.FC<PlaygroundTabProps> = ({
  newPrompt,
  playgroundData,
  setPlaygroundData,
  isEnhanced,
  hasBeenEnhanced,
  isEnhancing,
  isStreaming,
  smoothEnabled,
  smoothStreamText,
  handlePlaygroundEnhancePrompt,
  handleRevertToOriginal,
  handleToggleToEnhanced,
  handlePromptSubmit,
  enhancedPromptText,
  enhancementError,
  models,
  displayContent,
  smoothReset,
  playgroundPromptRef,
  userScrolledAwayRef,
  onCopyText,
}) => {
  const { t } = useLanguage();

  // Ensure systemMessage initialized from displayContent for existing prompts
  useEffect(() => {
    if (!newPrompt && !playgroundData.systemMessage && displayContent.systemprompt) {
      setPlaygroundData(prev => ({ ...prev, systemMessage: displayContent.systemprompt }));
    }
  }, [newPrompt, playgroundData.systemMessage, displayContent.systemprompt, setPlaygroundData]);

  return (
    <div className="flex flex-col lg:flex-row h-full w-full">
      <div className="flex-1 p-6 space-y-6">
        <div>
          {/* Title */}
          <label
            htmlFor="playground-title"
            className="block text-sm font-semibold mb-2 text-left text-gray-700"
          >
            {t("title")}
          </label>
          <input
            id="playground-title"
            type="text"
            value={playgroundData.title || displayContent.title}
            onChange={(e) =>
              setPlaygroundData((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            className="w-full px-4 py-3 border rounded-lg text-left text-gray-900 border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
            placeholder={
              t("promptTitlePlaceholder") || "A concise title for your prompt"
            }
          />
        </div>
        <div>
          <label
            htmlFor="playground-description"
            className="block text-sm font-semibold mb-2 text-left text-gray-700"
          >
            {t("shortDescription")}
          </label>
          <textarea
            id="playground-description"
            value={playgroundData.description || displayContent.description}
            onChange={(e) =>
              setPlaygroundData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            rows={3}
            className="w-full px-4 py-3 border rounded-lg text-left text-gray-900 border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
            placeholder={
              t("shortDescriptionPlaceholder") ||
              "Brief summary of what the prompt does"
            }
          />
        </div>
        {!newPrompt && (
          <div>
            <label
              htmlFor="system-prompt"
              className="block text-sm font-semibold mb-2 text-left text-gray-700"
            >
              {t("systemPrompt")}
            </label>
            <textarea
              id="system-prompt"
              value={playgroundData.systemMessage}
              onChange={(e) =>
                setPlaygroundData(prev => ({ ...prev, systemMessage: e.target.value }))
              }
              rows={4}
              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue text-sm"
              placeholder={t("systemPrompt")}
            />
          </div>
        )}
        <div>
          {!newPrompt ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="prompt-text"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("prompt")}
                </label>
                <div className="flex items-center space-x-2">
                  {onCopyText && (
                    <SmallCopyButton
                      onCopy={() => onCopyText(playgroundData.promptText, "Prompt")}
                      label={t("copy")}
                    />
                  )}
                  {isEnhanced && (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 border border-green-300 rounded-full">
                      <svg
                        className="mr-1 h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {t("enhancedVersion")}
                    </span>
                  )}
                </div>
              </div>
              <textarea
                id="prompt-text"
                ref={playgroundPromptRef}
                value={
                  smoothEnabled && isStreaming
                    ? smoothStreamText || playgroundData.promptText
                    : playgroundData.promptText
                }
                style={{ whiteSpace: "pre-wrap" }}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const distanceFromBottom =
                    el.scrollHeight - (el.scrollTop + el.clientHeight);
                  userScrolledAwayRef.current = distanceFromBottom > 140;
                }}
                onChange={(e) => {
                  setPlaygroundData((prev) => ({
                    ...prev,
                    promptText: e.target.value,
                  }));
                  if (isEnhanced || hasBeenEnhanced) {
                    // Reset enhancement flags & cached messages
                    setPlaygroundData((prev) => ({
                      ...prev,
                      originalSystemMessage: "",
                      enhancedSystemMessage: "",
                    }));
                  }
                  if (smoothEnabled) smoothReset();
                }}
                rows={8}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue font-mono text-sm ${
                  isEnhanced ? "border-green-300 bg-green-50" : "border-gray-300"
                }`}
                placeholder="Enter your prompt here..."
              />
              <div className="flex items-center justify-between mt-1">
                <CharacterCount
                  text={playgroundData.promptText}
                  className="text-sm text-gray-500"
                />
                {enhancementError && (
                  <span className="text-xs text-red-600">
                    {enhancementError}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 my-4">
                <button
                  className="px-4 py-2 bg-brand-dark-blue text-white rounded-md font-medium hover:bg-brand-electric-blue disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isEnhancing}
                  onClick={handlePlaygroundEnhancePrompt}
                >
                  {isEnhancing ? (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "✨"
                  )}
                  {isEnhancing ? t("enhancing") : t("enhance")}
                </button>
                {isEnhanced ? (
                  <button
                    className="text-sm text-gray-600 underline hover:text-indigo-600"
                    onClick={handleRevertToOriginal}
                  >
                    {t("showOriginal")}
                  </button>
                ) : hasBeenEnhanced && enhancedPromptText ? (
                  <button
                    className="text-sm text-gray-600 underline hover:text-indigo-600"
                    onClick={handleToggleToEnhanced}
                  >
                    {t("showEnhanced")}
                  </button>
                ) : null}
                <button
                  className="ml-auto px-4 py-2 bg-brand-electric-blue text-white rounded-md font-medium hover:bg-brand-dark-blue disabled:bg-gray-300"
                  disabled={!playgroundData.promptText.trim()}
                  onClick={handlePromptSubmit}
                >
                  {t("saveAsNewVersion")}
                </button>
              </div>
            </>
          ) : null}
        </div>
        {!newPrompt && (
          <div>
            <label
              htmlFor="change-notes"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("changeNotes")}
            </label>
            <textarea
              id="change-notes"
              value={playgroundData.changeNotes}
              onChange={(e) =>
                setPlaygroundData((prev) => ({
                  ...prev,
                  changeNotes: e.target.value,
                }))
              }
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
              placeholder={t("promptChangeNotesPlaceholder")}
            />
          </div>
        )}
      </div>
      <div className="w-full lg:w-80 p-6 bg-gray-50 border-l border-gray-200 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3">
            {t("model")}
          </h3>
          <select
            className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
            value={playgroundData.selectedModel}
            onChange={(e) =>
              setPlaygroundData((prev) => ({
                ...prev,
                selectedModel: e.target.value,
              }))
            }
          >
            {models.map((model) => (
              <option key={model.id} value={model.name}>
                {model.display_name}
              </option>
            ))}
          </select>
          {playgroundData.selectedModel &&
            models.find((m) => m.name === playgroundData.selectedModel) && (
              <>
                <div className="text-xs text-gray-500 mb-1">
                  {models
                    .find((m) => m.name === playgroundData.selectedModel)
                    ?.context_window.toLocaleString()} {" "}
                  tokens
                </div>
                <div className="text-xs text-gray-600">
                  $
                  {models
                    .find((m) => m.name === playgroundData.selectedModel)
                    ?.input_cost_per_million_tokens?.toFixed(2) || "0.00"} {" "}
                  / $
                  {models
                    .find((m) => m.name === playgroundData.selectedModel)
                    ?.output_cost_per_million_tokens?.toFixed(2) || "0.00"}
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default PlaygroundTab;
